import { useEffect, useState, useMemo, useRef } from 'react';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  RowData,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import noop from 'lodash/noop';
import {
  CustomCell,
  ComplexModelResultItem,
  ContentAlignment,
  DataFormatter,
  DataGridProps,
  SPACING,
  TableColumn,
  ValueType,
  SortOrder,
} from './models';
import {
  getColumnVisibilityState,
  getHeaderCase,
  getColumnValueTypeFromData,
  getContentAlignment,
} from './DataGridUtils';
import { Grid } from './Grid';
import { CellRenderer } from './CellRenderer';
import { DEFAULT_CUSTOM_STYLES } from './DataGridConstants';
import { applyCustomBgColor, parseDateTime } from '../../../utils';
import { useRowVirtualizer } from '../DataTable/hooks';
import EmptyBox from '../EmptyBox';
import { Layout, utils } from '@appd/uik';

// Setting these to optional so they don't interfere with declaration merging in DataTable
// DataTable will eventually replace DataGrid
declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    cellTemplate?: CustomCell;
    sortBy?: string;
    sortable?: boolean;
    valueType?: ValueType;
    contentAlignment?: ContentAlignment;
    cellRenderer?: CustomCell;
    formatData?: boolean;
    dataFormatter?: DataFormatter;
  }

  interface TableMeta<TData extends RowData> {
    spacing?: SPACING;
    since?: number;
    until?: number;
  }
}

const emptyData = [] as Array<{ [k: string]: any }>;
const emptyColDefs = [] as Array<TableColumn>;

const DataGrid = ({
  data,
  dataModel,
  metadata,
  colDefs,
  sortBy,
  orderBy,
  backgroundColor,
  className,
  formatHeader = false,
  onRowClick = noop,
  onCellClick = noop,
  fetchNextPage = noop,
  isFetching = false,
  isFetchingNextPage = false,
  height = 300,
  styles = DEFAULT_CUSTOM_STYLES,
  onSortClick = noop,
  spacing = SPACING.COMPACT,
  useRowVirtualization = false,
  selectedRowIndex = -1,
}: DataGridProps) => {
  // if backgroundColor is not explicitly specified, use bg-inherit (overrides background color specified in className)
  const customClassName = applyCustomBgColor(className, backgroundColor);
  const model = (dataModel?.fields?.[0] as ComplexModelResultItem)?.model ?? dataModel;
  const columnHelper = createColumnHelper();

  const columns: ColumnDef<unknown, any>[] = useMemo(
    () =>
      // use data to construct columns (each row of data should have the same columns, so use the first row)
      Array.isArray(data) && data.length > 0
        ? Object.keys(data[0]).map(k => {
            const valueType =
              colDefs?.find(colDef => colDef.field === k)?.valueType ?? // use column definition
              (model?.fields?.find(field => field.alias === k)?.type as ValueType) ?? // use UQL result model
              getColumnValueTypeFromData(data, k); // use table data
            return columnHelper.accessor(k, {
              cell: CellRenderer,
              header: props => {
                const currentColDef = colDefs?.find(colDef => colDef.field === props.header.id);
                const columnHeader = currentColDef?.headerName ?? props.header.id;
                return formatHeader ? getHeaderCase(columnHeader) : columnHeader;
              },
              meta: {
                cellTemplate: colDefs?.find(colDef => colDef.field === k)?.cellTemplate,
                sortBy: colDefs?.find(colDef => colDef.field === k)?.sortBy,
                sortable: colDefs?.find(colDef => colDef.field === k)?.sortable ?? false,
                valueType,
                contentAlignment:
                  colDefs?.find(colDef => colDef.field === k)?.align ?? getContentAlignment(valueType as ValueType),
                cellRenderer: colDefs?.find(colDef => colDef.field === k)?.cellRenderer,
                formatData: colDefs?.find(colDef => colDef.field === k)?.formatData ?? true,
                dataFormatter: colDefs?.find(colDef => colDef.field === k)?.dataFormatter,
              },
            });
          })
        : [],
    [data],
  ) as ColumnDef<unknown, any>[];

  // initialize sorting state for grid
  const [sortingState, setSortingState] = useState<SortingState>(
    sortBy && orderBy ? [{ id: sortBy, desc: orderBy === SortOrder.DESC }] : [],
  );

  useEffect(() => {
    if (sortingState.length > 0) {
      const { id, desc } = sortingState[0];
      onSortClick({ orderBy: desc ? SortOrder.DESC : SortOrder.ASC, sortBy: id });
    }
  }, [sortingState]);
  const visibilityState = useMemo(() => (colDefs ? getColumnVisibilityState(colDefs) : {}), [colDefs]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(visibilityState);

  const table = useReactTable({
    columns,
    data: data ?? emptyData,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    state: {
      sorting: sortingState,
    },
    onSortingChange: setSortingState,
    initialState: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      ...{ spacing },
      // Calculate start and end timestamp in UNIX from metadata (if available) here so calculation is done once for a table
      ...(metadata?.since ? { since: parseDateTime(metadata.since as string) } : {}),
      ...(metadata?.until ? { until: parseDateTime(metadata.until as string) } : {}),
      ...{ rowVirtualization: { enabled: useRowVirtualization } },
    },
  });

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { rows } = table.getRowModel();
  const { virtualRows, paddingTop, paddingBottom } = useRowVirtualizer(
    table,
    tableContainerRef as React.MutableRefObject<HTMLDivElement>,
  );
  const tableRows = virtualRows?.map(virtualRow => rows[virtualRow.index]) ?? rows;

  if (!data || data.length === 0) {
    return <EmptyBox />;
  }

  return (
    <div
      className={[customClassName, (onRowClick !== utils.noop || onCellClick !== utils.noop) && Layout.$.ACTIONABLE]
        .filter(Boolean)
        .join(' ')}
      ref={tableContainerRef}
      style={{ height, overflow: 'auto' }}
    >
      <Grid
        table={table}
        tableRows={tableRows}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        colDefs={colDefs ?? emptyColDefs}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onScroll={fetchNextPage}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        height={height}
        styles={styles}
        spacing={spacing}
        selectedRowIndex={selectedRowIndex}
      />
    </div>
  );
};

export { DataGrid };
